import { ApolloLink, Observable } from "apollo-link";
import { Operation } from "apollo-boost";
import { ErrorLink, onError } from "apollo-link-error";
import { ServerError } from "apollo-link-http-common";
import * as Sentry from "@sentry/browser";

type LogoutOptions = { redirect_uri: string; brand: string };

export const isServerError = (
  error: Error | ServerError | undefined,
): error is ServerError =>
  typeof error !== "undefined" &&
  typeof (error as { statusCode?: unknown }).statusCode === "number";

export const getOperationName = (operation: Operation) => {
  const operationTypeDetails = operation.query.definitions.find(
    (definition) => definition.kind === "OperationDefinition",
  ) as { operation?: string } | undefined;
  return `${operationTypeDetails?.operation || "unknown operation"} ${
    operation.operationName
  }`;
};

export interface CreateAuthErrorLinkOptions {
  getTokenSilently: () => Promise<string>;
  setToken: (token: string) => void;
  logout: (opts: LogoutOptions) => void;
  getBrand: () => string;
}

export const createAuthErrorLink = ({
  getTokenSilently,
  setToken,
  logout,
  getBrand,
}: CreateAuthErrorLinkOptions): ApolloLink =>
  onError((({ networkError, operation, forward }) => {
    if (isServerError(networkError) && networkError.statusCode === 401) {
      Sentry.addBreadcrumb({
        type: "error",
        category: "fetch",
        message: "401 encountered, attempting to reauthenticate...",
        data: {
          errorMessage: networkError.message,
          operation: getOperationName(operation),
          variables: operation.variables,
          headers: operation.getContext().headers,
          status: networkError.statusCode,
        },
        level: "warning",
        timestamp: Date.now() / 1000,
      });

      return new Observable((observer) => {
        getTokenSilently()
          .then((token: string) => {
            setToken(token);
          })
          .then(() => {
            Sentry.addBreadcrumb({
              type: "info",
              category: "auth",
              message: "Successfully reauthenticated, retrying failed request",
              level: "info",
              timestamp: Date.now() / 1000,
            });

            const subscriber = {
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer),
            };

            forward(operation).subscribe(subscriber);
          })
          .catch((error: Error) => {
            Sentry.addBreadcrumb({
              type: "info",
              category: "auth",
              message: "Failed to silently reauth, logging the user out",
              data: { error },
              level: "info",
              timestamp: Date.now() / 1000,
            });

            // Do not propagate the error to the subscriber: logout() will
            // redirect the user, and any pending fire-and-forget queries
            // (e.g. ContentPlayer's unmount progress refresh) would otherwise
            // surface this rejection as an unhandled "Network error: Login
            // required" in Sentry. See Sentry issue 3078139911.
            logout({
              redirect_uri: window.location.href,
              brand: getBrand(),
            });
          });
      });
    }

    if (networkError) {
      Sentry.addBreadcrumb({
        type: "error",
        category: "fetch",
        message: networkError.toString(),
        data: {
          operation: getOperationName(operation),
          variables: operation.variables,
          headers: operation.getContext().headers,
        },
        level: "error",
        timestamp: Date.now() / 1000,
      });
    }
  }) as ErrorLink.ErrorHandler);
